(() => {
  const money = (cents, currency = null) => {
    const n = Number(cents) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency || (window.Shopify?.currency?.active || "USD"),
      }).format(n);
    } catch {
      return `${n.toFixed(2)}`;
    }
  };

  const getRootUrl = () => {
    const routes = window.Shopify?.routes;
    if (routes?.root) return routes.root;
    if (routes?.root_url) return routes.root_url;
    return "/";
  };

  class ShoppableFeedPortable {
    constructor(sectionEl) {
      this.sectionEl = sectionEl;
      this.track = sectionEl.querySelector(".sf-track");
      this.prevBtn = sectionEl.querySelector(".sf-arrow--prev");
      this.nextBtn = sectionEl.querySelector(".sf-arrow--next");
      this.progressBar = sectionEl.querySelector(".sf-progress__bar");

      this.modal = sectionEl.querySelector(".sf-modal");
      this.modalVariantSelect = sectionEl.querySelector(".sf-modal__select");
      this.modalTitle = sectionEl.querySelector(".sf-modal__title");
      this.modalPrice = sectionEl.querySelector(".sf-modal__price");
      this.modalMedia = sectionEl.querySelector(".sf-modal__media");
      this.modalLink = sectionEl.querySelector(".sf-modal__link");
      this.modalForm = sectionEl.querySelector(".sf-modal__form");
      this.modalStatus = sectionEl.querySelector(".sf-modal__status");

      this.currentVideo = null;
      this.productRotators = [];
      this._io = null;

      this._bindCarousel();
      this._bindRevealAnimations();
      this._bindMedia();
      this._bindProducts();
      this._bindModal();
    }

    _bindRevealAnimations() {
      const els = [...this.sectionEl.querySelectorAll(".sf-reveal")];
      if (!els.length) return;

      // If browser doesn't support IO, show everything.
      if (!("IntersectionObserver" in window)) {
        els.forEach((el) => el.classList.add("is-visible"));
        return;
      }

      const io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (!e.isIntersecting) continue;
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        },
        { threshold: 0.15, rootMargin: "0px 0px -10% 0px" }
      );

      els.forEach((el) => io.observe(el));
    }

    _bindCarousel() {
      if (!this.track) return;

      const scrollBySlide = (dir) => {
        const slide = this.track.querySelector(".sf-slide");
        const delta = slide ? slide.getBoundingClientRect().width + 15 : this.track.clientWidth;
        this.track.scrollBy({ left: dir * delta, behavior: "smooth" });
      };

      this.prevBtn?.addEventListener("click", () => scrollBySlide(-1));
      this.nextBtn?.addEventListener("click", () => scrollBySlide(1));

      const updateProgress = () => {
        if (!this.progressBar) return;
        const max = this.track.scrollWidth - this.track.clientWidth;
        const ratio = max <= 0 ? 1 : Math.min(1, Math.max(0, this.track.scrollLeft / max));
        this.progressBar.style.width = `${Math.round(ratio * 100)}%`;
      };

      this.track.addEventListener("scroll", () => {
        updateProgress();
        // Pause video when user swipes away
        this._pauseCurrentVideo();
      }, { passive: true });

      updateProgress();
      window.addEventListener("resize", updateProgress);
    }

    _bindMedia() {
      const cards = [...this.sectionEl.querySelectorAll(".sf-card")];
      if (!cards.length) return;

      // Autoplay first media when section enters view (if present)
      const autoplayVideo = this.sectionEl.querySelector('video[data-autoplay="true"]');
      if (autoplayVideo) {
        this._io = new IntersectionObserver((entries) => {
          for (const e of entries) {
            if (!e.isIntersecting) continue;
            const card = autoplayVideo.closest(".sf-card");
            if (!card) return;
            this._playVideoInCard(card, { forceMute: true });
          }
        }, { threshold: 0.4 });
        this._io.observe(this.sectionEl);
      }

      cards.forEach((card) => {
        const video = card.querySelector("video.sf-video");
        if (!video) return;

        const playBtn = card.querySelector(".sf-play");
        const audioBtn = card.querySelector(".sf-audio");

        // Click play overlay
        playBtn?.addEventListener("click", () => this._playVideoInCard(card));

        // Pause when overlay is clicked while playing
        card.addEventListener("click", (ev) => {
          const target = ev.target;
          if (!(target instanceof Element)) return;
          if (target.closest(".sf-play") || target.closest(".sf-audio") || target.closest(".sf-product")) return;
          if (card.dataset.playing === "true") this._pauseVideoInCard(card);
        });

        // Hover play support (based on data on section via Liquid setting)
        const playOn = this.sectionEl.dataset.playMediaOn;
        if (playOn === "play_on_hover") {
          card.addEventListener("mouseenter", () => {
            if (card.dataset.playing === "true") return;
            this._playVideoInCard(card, { respectAudioBtn: true });
          });
          card.addEventListener("mouseleave", () => this._pauseVideoInCard(card));
        }

        audioBtn?.addEventListener("click", (e) => {
          e.stopPropagation();
          const state = audioBtn.getAttribute("data-audio-state") || "mute";
          const next = state === "mute" ? "unmute" : "mute";
          audioBtn.setAttribute("data-audio-state", next);
          video.muted = next !== "unmute";
        });
      });

      // Pause if user scrolls page and section leaves view
      const pauseOnLeave = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) continue;
          this._pauseCurrentVideo();
        }
      }, { threshold: 0.01 });
      pauseOnLeave.observe(this.sectionEl);
    }

    _playVideoInCard(card, opts = {}) {
      const video = card.querySelector("video.sf-video");
      if (!video) return;

      // Ensure only one plays at a time
      if (this.currentVideo && this.currentVideo !== video) {
        this._pauseCurrentVideo();
      }

      const audioBtn = card.querySelector(".sf-audio");
      const audioState = audioBtn?.getAttribute("data-audio-state") || "mute";
      const shouldUnmute = opts.forceMute ? false : (audioState === "unmute");
      video.muted = !shouldUnmute;

      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          // Autoplay might be blocked; keep UI consistent
          this._pauseVideoInCard(card);
        });
      }

      card.dataset.playing = "true";
      this.currentVideo = video;
    }

    _pauseVideoInCard(card) {
      const video = card.querySelector("video.sf-video");
      if (!video) return;
      video.pause();
      card.dataset.playing = "false";
      if (this.currentVideo === video) this.currentVideo = null;
    }

    _pauseCurrentVideo() {
      if (!this.currentVideo) return;
      const card = this.currentVideo.closest(".sf-card");
      if (card) this._pauseVideoInCard(card);
      else this.currentVideo.pause();
      this.currentVideo = null;
    }

    _bindProducts() {
      // Rotate products inside each slide (smooth right-to-left)
      const viewports = [...this.sectionEl.querySelectorAll(".sf-products__viewport[data-rotate='true']")];
      viewports.forEach((vp) => {
        const items = [...vp.querySelectorAll(".sf-product")];
        if (items.length <= 1) return;
        const DURATION_MS = 360;
        let idx = 0;
        let animating = false;

        items.forEach((el, i) => {
          el.style.display = i === 0 ? "grid" : "none";
          el.classList.toggle("sf-product--active", i === 0);
          el.classList.remove("sf-product--enter", "sf-product--exit");
        });

        const id = window.setInterval(() => {
          if (animating) return;
          animating = true;

          const prev = items[idx];
          const nextIdx = (idx + 1) % items.length;
          const next = items[nextIdx];

          // Prepare next
          next.style.display = "grid";
          next.classList.remove("sf-product--active", "sf-product--exit");
          next.classList.add("sf-product--enter");
          // Force reflow so animation starts reliably
          void next.offsetWidth;

          // Animate prev out
          prev.classList.remove("sf-product--active", "sf-product--enter");
          prev.classList.add("sf-product--exit");

          let donePrev = false;
          let doneNext = false;
          const finish = () => {
            if (!donePrev || !doneNext) return;

            // Cleanup prev
            prev.style.display = "none";
            prev.classList.remove("sf-product--exit");

            // Promote next to active
            next.classList.remove("sf-product--enter");
            next.classList.add("sf-product--active");

            idx = nextIdx;
            animating = false;
          };

          const onEndPrev = (e) => {
            if (e.target !== prev) return;
            donePrev = true;
            finish();
          };
          const onEndNext = (e) => {
            if (e.target !== next) return;
            doneNext = true;
            finish();
          };

          prev.addEventListener("animationend", onEndPrev, { once: true });
          next.addEventListener("animationend", onEndNext, { once: true });

          // Fallback for mobile browsers that sometimes miss animationend
          window.setTimeout(() => {
            donePrev = true;
            doneNext = true;
            finish();
          }, DURATION_MS + 80);
        }, 4000);
        this.productRotators.push(id);
      });

      // Quick view / product page actions
      this.sectionEl.addEventListener("click", async (e) => {
        const target = e.target;
        if (!(target instanceof Element)) return;
        const btn = target.closest(".sf-product__title");
        if (!btn) return;

        const handle = btn.getAttribute("data-handle");
        const action = btn.getAttribute("data-action");
        if (!handle) return;

        if (action === "product_page") {
          window.location.href = `${getRootUrl()}products/${handle}`;
          return;
        }

        await this._openQuickView(handle);
      });
    }

    _bindModal() {
      if (!this.modal) return;
      this.modal.addEventListener("click", (e) => {
        const target = e.target;
        if (!(target instanceof Element)) return;
        if (target.closest("[data-close]")) this._closeModal();
      });

      document.addEventListener("keydown", (e) => {
        if (e.key !== "Escape") return;
        if (!this.modal || this.modal.hidden) return;
        this._closeModal();
      });

      this.modalForm?.addEventListener("submit", async (e) => {
        e.preventDefault();
        const id = this.modalVariantSelect?.value;
        if (!id) return;
        await this._addToCart(id);
      });
    }

    async _openQuickView(handle) {
      if (!this.modal) return;
      this.modalStatus.textContent = "Loading…";
      this._showModal();

      try {
        const res = await fetch(`${getRootUrl()}products/${handle}.js`, { credentials: "same-origin" });
        if (!res.ok) throw new Error("Product fetch failed");
        const product = await res.json();

        this.modalTitle.textContent = product.title || "";
        this.modalLink.href = product.url || `${getRootUrl()}products/${handle}`;
        this.modalPrice.textContent = money(product.price, window.Shopify?.currency?.active);

        this.modalMedia.innerHTML = "";
        if (product.images && product.images[0]) {
          const img = document.createElement("img");
          img.src = product.images[0];
          img.alt = product.title || "";
          this.modalMedia.appendChild(img);
        }

        // variants
        this.modalVariantSelect.innerHTML = "";
        product.variants.forEach((v) => {
          const opt = document.createElement("option");
          opt.value = String(v.id);
          opt.textContent = v.public_title || v.title;
          opt.disabled = !v.available;
          this.modalVariantSelect.appendChild(opt);
        });

        // Default to first available
        const firstAvailable = product.variants.find((v) => v.available) || product.variants[0];
        if (firstAvailable) this.modalVariantSelect.value = String(firstAvailable.id);

        this.modalStatus.textContent = "";
      } catch (err) {
        this.modalStatus.textContent = "Could not load product.";
      }
    }

    async _addToCart(variantId) {
      this.modalStatus.textContent = "Adding…";
      try {
        const res = await fetch(`${getRootUrl()}cart/add.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
          credentials: "same-origin",
        });
        if (!res.ok) throw new Error("Add to cart failed");
        this.modalStatus.textContent = "Added to cart.";
      } catch {
        this.modalStatus.textContent = "Could not add to cart.";
      }
    }

    _showModal() {
      this.modal.hidden = false;
      document.documentElement.style.overflow = "hidden";
    }

    _closeModal() {
      if (!this.modal) return;
      this.modal.hidden = true;
      document.documentElement.style.overflow = "";
    }
  }

  const init = () => {
    document.querySelectorAll(".sf[data-section-id]").forEach((el) => {
      if (el.__sfInit) return;
      el.__sfInit = true;
      new ShoppableFeedPortable(el);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
